import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { connect } from 'react-redux';

import { uninstallTile } from 'actions/gallery';

import AppShapedImage from '../AppShapedImage/index';
import Typography from '../Typography';
import FontIcon from '../FontIcon';
import App from '../../../models/app';

import { UninstallTileModal } from '../StateManagementButton/modals/UninstallTileModal';
import { TooFewTilesModal } from '../StateManagementButton/modals/TooFewTilesModal';

import { DEVICE_RENAME } from 'config';
import { minTileNumber, roundEdgeDevices } from '../../../constants';

const RENAME: any = DEVICE_RENAME;

type ReduxProps = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps>;
interface TileCardProps extends ReduxProps {
  tile: App;
  tileCount: number;
}

const RowWrapper = styled.div`
  background: var(--sys-color--surface);
  border-radius: 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: 8px 12px;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
`;

const TruncatedTypography = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  word-break: break-word;
  flex-grow: 1;
  text-align: left;
`;

const DragIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: grab;
  flex-shrink: 0;
  &:active {
    cursor: grabbing;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: inherit;
  flex-shrink: 0;
`;

const mapStateToProps = (state: any) => {
  const deviceName = state.registry.currentUser.device?.productName?.toLowerCase();
  return {
    deviceName: RENAME[deviceName] || deviceName
  }
};

const mapDispatchToProps = (dispatch: any) => ({
  removeTile: (tileId: string) => dispatch(uninstallTile(tileId)),
});

const TileCard: FC<TileCardProps> = ({
  tile,
  removeTile,
  deviceName,
  tileCount
}) => {

  const [state, updateState] = useState({ showModal: false, loading: false });

  const onCancel = () => {
    updateState({ ...state, showModal: false })
  }

  const onSuccess = async () => {
    await updateState({ ...state, loading: true })
    await removeTile(tile.id)
    await updateState({ ...state, showModal: false, loading: false })
  }

  const renderRemoveModal = () => (
    tileCount > minTileNumber ? (
      <UninstallTileModal
        app={tile}
        onCancel={onCancel}
        onSuccess={onSuccess}
        loading={state.loading}
      />
    ) : (
      <TooFewTilesModal
        app={tile}
        onCancel={onCancel}
        onSuccess={() => { }}
        loading={state.loading}
      />
    )
  )

  const showModal = () => {
    updateState({ ...state, showModal: true })
  }

  return (
    <RowWrapper>
      <DragIconWrapper
        id={`tile-dots-${tile.id}`}
        aria-label={`tile-dots-${tile.id}`}
      >
        <FontIcon
          name="drag_handle"
          color="var(--sys-color--on-surface)"
          size={24}
        />
      </DragIconWrapper>
      <AppShapedImage
        photoProperties={{
          src: tile.banners?.[0]?.uri || '',
          alt: tile.name,
          width: tile.banners?.[0]?.width,
          height: tile.banners?.[0]?.height,
          isSquircle: roundEdgeDevices.includes(deviceName?.toLowerCase()),
        }}
        width={90}
        height={90}
        borderRadius="16px"
        borderReservedSpace={false}
      />
      <TruncatedTypography variant="labelLarge" color="var(--sys-color--on-surface)">
        {tile.name}
      </TruncatedTypography>
      <IconButton
        id={`tile-delete-${tile.id}`}
        aria-label={`tile-delete-${tile.id}`}
        onClick={() => showModal()}
      >
        <FontIcon
          name="delete"
          color="var(--sys-color--on-surface)"
          size={24}
        />
      </IconButton>
      {state.showModal && renderRemoveModal()}
    </RowWrapper>
  );
}

export default connect(mapStateToProps, mapDispatchToProps)(TileCard);
